/**
 * useWorkforceOverviewAccess Hook
 *
 * V70.27: 현재 사용자가 `조직관리 > 인력 현황` 에 접근 가능한지 확인
 *
 * 접근 정책 (서버 SSoT = `getMyWorkforceOverviewAccess` callable)
 *  - Admin(ADMIN_LIKE_ROLES, 서버 `checkIsAdminLike`) → 별도 등록 없이 접근 가능
 *  - 그 외 → `workforceOverviewSettings/default.allowedUserIds` 에 등록된 경우만 접근 가능
 *
 * 설계 근거
 *  - `useIsOrgApprover` (V168/V169.2) 와 동일 패턴: 역할 기반 RBAC 로 표현할 수 없는
 *    "사용자 명시 등록" 권한을 서버 조회 + 모듈 레벨 TTL 캐시로 처리한다.
 *  - 권한 판정은 FE state/localStorage 가 아니라 **서버(callable)** 가 수행한다.
 *    FE 는 결과만 사용하며, 실제 데이터 접근 시에도 BE 가 다시 검증한다.
 *
 * 사용처: Sidebar 메뉴 노출 / WorkforceOverviewAccessGuard (라우트 가드)
 */
import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import { useCurrentUser } from './useCurrentUser';

interface AccessResponse {
  success: boolean;
  canAccess: boolean;
  isAdmin: boolean;
  isAllowedUser: boolean;
  visibleOrgUnitCount: number;
  visibleOrgUnitIds: string[];
}

export interface UseWorkforceOverviewAccessResult {
  /** 인력 현황 페이지 접근 가능 여부 */
  canAccess: boolean;
  /** Admin 경유 접근인지 */
  isAdmin: boolean;
  /** 명시 등록 사용자인지 */
  isAllowedUser: boolean;
  /** 리포트 대상 조직 수 */
  visibleOrgUnitCount: number;
  /** 리포트 대상 조직 ID (향후 리포트 조회에 사용) */
  visibleOrgUnitIds: string[];
  isLoading: boolean;
  error: Error | null;
  /** 설정 변경 후 강제 재조회 */
  refetch: () => void;
}

// ===== 모듈 레벨 TTL 캐시 (useIsOrgApprover V169.2 패턴) =====
const CACHE_TTL_MS = 30_000;
let cache: { uid: string; data: AccessResponse; ts: number } | null = null;
let pending: Promise<AccessResponse> | null = null;

const EMPTY: AccessResponse = {
  success: true, canAccess: false, isAdmin: false, isAllowedUser: false,
  visibleOrgUnitCount: 0, visibleOrgUnitIds: [],
};

async function fetchAccess(uid: string, force: boolean): Promise<AccessResponse> {
  if (!force && cache && cache.uid === uid && Date.now() - cache.ts < CACHE_TTL_MS) {
    return cache.data;
  }
  if (!force && pending) return pending;
  if (!functions) return EMPTY;

  const fn = httpsCallable<{}, AccessResponse>(functions, 'getMyWorkforceOverviewAccess');
  const promise = fn({})
    .then((r) => {
      const data = r.data || EMPTY;
      cache = { uid, data, ts: Date.now() };
      return data;
    })
    .finally(() => { pending = null; });

  pending = promise;
  return promise;
}

/** 설정 저장 직후 등 즉시 반영이 필요할 때 캐시 무효화 */
export function invalidateWorkforceOverviewAccessCache(): void {
  cache = null;
  pending = null;
}

export function useWorkforceOverviewAccess(): UseWorkforceOverviewAccessResult {
  const { user, isLoading: userLoading } = useCurrentUser();
  const [data, setData] = useState<AccessResponse>(EMPTY);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [tick, setTick] = useState(0);

  const uid = user?.profile?.id;

  useEffect(() => {
    if (userLoading) return;
    if (!uid) { setData(EMPTY); setIsLoading(false); return; }

    let aborted = false;
    (async () => {
      try {
        setIsLoading(true);
        setError(null);
        const res = await fetchAccess(uid, tick > 0);
        if (!aborted) setData(res);
      } catch (err: any) {
        // permission-denied 등은 "접근 불가"로 처리 (화면이 깨지지 않도록 fail-closed)
        if (!aborted) { setData(EMPTY); setError(err as Error); }
      } finally {
        if (!aborted) setIsLoading(false);
      }
    })();
    return () => { aborted = true; };
  }, [uid, userLoading, tick]);

  return {
    canAccess: data.canAccess,
    isAdmin: data.isAdmin,
    isAllowedUser: data.isAllowedUser,
    visibleOrgUnitCount: data.visibleOrgUnitCount,
    visibleOrgUnitIds: data.visibleOrgUnitIds || [],
    isLoading: userLoading || isLoading,
    error,
    refetch: () => { invalidateWorkforceOverviewAccessCache(); setTick((t) => t + 1); },
  };
}
